import type { ReactNode } from 'react';
import React from 'react';
import { XStack, YStack } from 'tamagui';

import { isEmpty } from '../../misc';
import { CloseButton } from '../buttons/CloseButton';
import { BodyLargeRegular, BodySmallRegular } from '../text/Typography';

const HEADER_BASE_HEIGHT = 20;
const HEADER_PADDING_TOP = HEADER_BASE_HEIGHT;
const HEADER_PADDING_BOTTOM = 12;
const HEADER_SIDE_MIN_SIZE = 40;

export const getSheetHeaderHeight = ({
    title,
    showCloseButton,
    leftAction,
    withPadding = true,
}: Pick<
    SheetHeaderProps,
    'title' | 'showCloseButton' | 'leftAction' | 'withPadding'
>) => {
    if (!isEmpty(title) || !!showCloseButton || leftAction != null) {
        return (
            HEADER_SIDE_MIN_SIZE +
            (withPadding ? HEADER_PADDING_TOP : 0) +
            (withPadding ? HEADER_PADDING_BOTTOM : 0)
        );
    }
    return withPadding ? HEADER_BASE_HEIGHT : 0;
};

interface SheetHeaderProps {
    title?: string;
    subtitle?: string;
    showCloseButton?: boolean;
    onClose?: () => void;
    leftAction?: ReactNode;
    titleLineHeight?: number;
    withPadding?: boolean;
}

export const SheetHeader = ({
    title,
    subtitle,
    showCloseButton = true,
    onClose,
    leftAction,
    titleLineHeight = 24,
    withPadding = true,
}: SheetHeaderProps) => {
    const shouldShowCloseButton = showCloseButton && onClose != null;
    const isEmptyHeader =
        !shouldShowCloseButton && leftAction == null && isEmpty(title);

    if (isEmptyHeader) {
        // Return an empty component for consistent vertical spacing
        return (
            <XStack
                height={withPadding ? HEADER_BASE_HEIGHT : 0}
                width="100%"
            />
        );
    }

    return (
        <XStack
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            paddingHorizontal={withPadding ? 20 : 0}
            paddingTop={withPadding ? HEADER_PADDING_TOP : 0}
            paddingBottom={withPadding ? HEADER_PADDING_BOTTOM : 0}
        >
            <YStack
                minWidth={HEADER_SIDE_MIN_SIZE}
                minHeight={HEADER_SIDE_MIN_SIZE}
                justifyContent="center"
                alignItems="flex-start"
            >
                {leftAction}
            </YStack>
            <YStack flex={1} alignItems="center" justifyContent="center">
                {title != null && title !== '' && (
                    <BodyLargeRegular
                        width="100%"
                        textAlign="center"
                        lineHeight={titleLineHeight}
                    >
                        {title}
                    </BodyLargeRegular>
                )}
                {subtitle != null && subtitle !== '' && (
                    <BodySmallRegular
                        width="100%"
                        textAlign="center"
                        color="$gray1000"
                    >
                        {subtitle}
                    </BodySmallRegular>
                )}
            </YStack>
            <YStack
                minWidth={HEADER_SIDE_MIN_SIZE}
                minHeight={HEADER_SIDE_MIN_SIZE}
                justifyContent="center"
                alignItems="flex-end"
            >
                {shouldShowCloseButton && (
                    <CloseButton onPress={onClose} background="gray" />
                )}
            </YStack>
        </XStack>
    );
};
